import {
  DueDisplayOptions,
  Task,
  Timeslot,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { isBefore, isSameDay, startOfDay } from 'date-fns';

import { getTaskDueOccurrence } from './get-task-due-occurrence';
import { getTaskPlannedOccurrenceForDate } from './get-task-planned-occurrence-for-date';
import { isNumber } from './is-number';
import { getActiveSchedule } from './schedules/get-active-schedule';
import { getScheduleCompletedOnDate } from './schedules/get-schedule-completed-on-date';
import { shouldDueBeShown } from './should-due-be-shown';

const allDueDisplayOptions = Object.values(DueDisplayOptions);

export type GetTaskDueAndTimeslotOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
  displayDate?: Timestamp;
  displayDue?: DueDisplayOptions[];
};

export type TaskDueAndTimeslot = {
  dueDate: Timestamp | undefined;
  timeslot: Timeslot | null | undefined;
  hasTimeslot: boolean;
};

export const getTaskDueAndTimeslot = (
  task: Task,
  {
    today,
    weekStartsOn,
    displayDate,
    displayDue = allDueDisplayOptions,
  }: GetTaskDueAndTimeslotOptions,
): TaskDueAndTimeslot => {
  const activeSchedule = getActiveSchedule(task.schedules, today);

  const plannedOccurrenceForDate = getTaskPlannedOccurrenceForDate(
    task,
    displayDate ?? today,
    { weekStartsOn, today },
  );

  const resolvePlannedOccurrence = () => {
    if (!plannedOccurrenceForDate || !activeSchedule) {
      return plannedOccurrenceForDate;
    }

    const isScheduleCompleted = getScheduleCompletedOnDate(
      activeSchedule,
      plannedOccurrenceForDate.date,
      { fullyCompleted: true },
    );

    if (isScheduleCompleted) {
      return undefined;
    }

    const isPlannedOccurrenceInActiveSchedule =
      activeSchedule.plannedOccurrences?.find((occurrence) =>
        isSameDay(occurrence.date, plannedOccurrenceForDate.date),
      );

    if (isPlannedOccurrenceInActiveSchedule) {
      return plannedOccurrenceForDate;
    }

    if (
      activeSchedule.startDate &&
      (isSameDay(
        plannedOccurrenceForDate.date,
        startOfDay(activeSchedule.startDate),
      ) ||
        !isBefore(
          plannedOccurrenceForDate.date,
          startOfDay(activeSchedule.startDate),
        ))
    ) {
      return plannedOccurrenceForDate;
    }

    return undefined;
  };

  const plannedOccurrence = resolvePlannedOccurrence();

  const dueOccurrence = getTaskDueOccurrence(task, {
    plannedOccurrence,
    displayDue,
    displayDate,
    today,
    weekStartsOn,
  });

  const plannedOccurrenceShown = plannedOccurrence?.date ?? dueOccurrence?.date;
  const fallbackDeadline =
    !plannedOccurrenceShown &&
    !task.schedules?.length &&
    task.endStrategy?.deadline
      ? task.endStrategy.deadline
      : undefined;

  const dueDate =
    (plannedOccurrenceShown &&
      shouldDueBeShown(plannedOccurrenceShown, displayDue, today)) ||
    (fallbackDeadline && shouldDueBeShown(fallbackDeadline, displayDue, today))
      ? plannedOccurrence?.date ?? dueOccurrence?.date ?? fallbackDeadline
      : undefined;

  const timeslot = plannedOccurrence
    ? plannedOccurrence.timeslot
    : activeSchedule
      ? activeSchedule.timeslot
      : task.timeslot;

  const hasTimeslot =
    !!timeslot && (isNumber(timeslot.startTime) || !!timeslot.duration);

  return { dueDate, timeslot, hasTimeslot };
};
